import type { FileRejection } from 'react-dropzone'
import { useCallback } from 'react'

import FileDropzone, {
  type TFileDropzoneFile,
  type TFileDropzoneInputFile,
} from '../../FileDropzone'
import type { TNewUploadFile } from '../../../hooks/useNewUpload'

type Props = {
  mainFile?: TNewUploadFile | null
  files: TNewUploadFile[]
  filesValidationError: string | null
  fileError: TNewUploadFile | undefined
  setFiles: (files: TNewUploadFile[]) => void
  submitError?: string
}

const MAX_FILE_SIZE = 1024 * 1024 * 25

const mapManualFile = (file: TFileDropzoneInputFile): TNewUploadFile => {
  const isTooLarge = file.size > MAX_FILE_SIZE
  const rejectReason = file.rejectReason
    ? file.rejectReason
    : isTooLarge
      ? 'File size is too large'
      : null

  return {
    id: null,
    name: file.name,
    size: file.size,
    fileBlob: file,
    isUploading: false,
    uploadProgress: null,
    isUploaded: false,
    isAccepted: !rejectReason,
    isRejected: Boolean(rejectReason),
    rejectReason,
    uploadErr: null,
    uploadUrl: null,
    isPreparing: null,
  }
}

const BuildManualFiles = (props: Props) => {
  const {
    mainFile,
    files,
    filesValidationError,
    fileError,
    setFiles,
    submitError,
  } = props

  const handleFilesAccepted = useCallback(
    (acceptedFilesToAdd: File[]) => {
      const acceptedFiles = acceptedFilesToAdd.map((file) =>
        mapManualFile(file as TFileDropzoneInputFile),
      )

      setFiles([...files, ...acceptedFiles])
    },
    [files, setFiles],
  )

  const handleFilesRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      const rejectedFiles = fileRejections.map(({ file, errors }) => {
        return mapManualFile(
          Object.assign(file, {
            rejectReason: errors[0]?.message,
          }) as TFileDropzoneInputFile,
        )
      })

      setFiles([...files, ...rejectedFiles])
    },
    [files, setFiles],
  )

  const handleFileRemove = useCallback(
    (index: number) => {
      setFiles(files.filter((_, fileIndex) => fileIndex !== index))
    },
    [files, setFiles],
  )

  const manualDropzoneFiles: TFileDropzoneFile[] = [
    ...(mainFile ? [mainFile] : []),
    ...files,
  ]

  return (
    <div className="col-span-5 border-r border-gray-200 bg-gray-50 p-6 sm:col-span-3 md:col-span-2">
      <h3 className="block text-sm font-medium leading-5 text-black">
        Additional files
      </h3>
      <p className="pb-4 text-sm text-gray-600">
        Upload any logs or supporting files.
      </p>
      <FileDropzone
        files={manualDropzoneFiles}
        onAccepted={handleFilesAccepted}
        onRejected={handleFilesRejected}
        onRemoveFile={(index) => {
          if (mainFile) {
            if (index === 0) {
              return
            }

            handleFileRemove(index - 1)
            return
          }

          handleFileRemove(index)
        }}
      />
      {fileError && (
        <p className="mt-2 text-sm text-red-600">
          {fileError.rejectReason ||
            (fileError.uploadErr instanceof Error
              ? fileError.uploadErr.message
              : 'There was an issue with one of the files')}
        </p>
      )}
      {!fileError && filesValidationError && (
        <p className="mt-2 text-sm text-red-600">{filesValidationError}</p>
      )}
      {submitError && (
        <p className="mt-2 text-sm text-red-600">{submitError}</p>
      )}
    </div>
  )
}

export default BuildManualFiles
